import { useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import FileUpload from './FileUpload';
import ScreenshotGuide from './ScreenshotGuide';
import { GRADES, computeGpa } from '../utils/gradePoints';
import { recognizeGrades } from '../utils/ocr';

// Use case 1: direct GPA calculation. Screenshot OCR is the primary input;
// manual entry is one toggle away and is auto-filled after a successful scan.
const GpaTab = () => {
  const [mode, setMode] = useState('screenshot');
  const [counts, setCounts] = useState({});
  const [gpa, setGpa] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState(null);

  const totalCourses = GRADES.reduce(
    (sum, grade) => sum + (parseInt(counts[grade.letter], 10) || 0),
    0
  );

  const setCount = (letter, value) => {
    const next = Math.max(0, Math.min(99, value));
    setCounts(prev => ({ ...prev, [letter]: next }));
  };

  const handleFile = async (file) => {
    setIsProcessing(true);
    setNotice(null);
    setProgress(0);
    try {
      const { counts: found, total } = await recognizeGrades(file, setProgress);
      if (total > 0) {
        setCounts(found);
        setGpa(computeGpa(found));
        setMode('manual');
        setNotice({
          tone: 'good',
          text: `Read ${total} grades from your screenshot. Adjust below if anything looks off.`,
        });
      } else {
        setNotice({
          tone: 'bad',
          text: 'No grades found in that image. Crop it to just the grades below the GRADE heading, as shown in the guide below.',
        });
      }
    } catch (error) {
      console.error('OCR Error:', error);
      setNotice({
        tone: 'bad',
        text: 'Something went wrong reading that image. Try a clearer screenshot.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <section className="rounded-[28px] bg-gradient-to-b from-graphite-900/95 to-graphite-700/90 backdrop-blur-xl border border-white/10 text-white px-5 pt-6 pb-7 sm:px-6 shadow-xl shadow-slate-900/25">
      <h2 className="text-[22px] font-semibold tracking-tight">GPA calculator</h2>
      <p className="mt-1 text-sm text-white/65">
        Scan your SOLS grades, or count them in by hand.
      </p>

      {/* screenshot / manual toggle */}
      <div className="mt-5 grid grid-cols-2 gap-1 rounded-full bg-white/10 p-1" role="tablist" aria-label="Input method">
        {[
          { id: 'screenshot', label: 'Screenshot' },
          { id: 'manual', label: 'Manual entry' },
        ].map(option => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={mode === option.id}
            onClick={() => setMode(option.id)}
            className={`py-2.5 rounded-full text-sm font-medium transition-colors ${
              mode === option.id ? 'bg-white text-slate-900' : 'text-white/70'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {notice && (
        <p
          className={`mt-4 text-sm rounded-2xl px-4 py-3 ${
            notice.tone === 'good'
              ? 'bg-mint-300/15 text-mint-200'
              : 'bg-white/10 text-gold-200'
          }`}
        >
          {notice.text}
        </p>
      )}

      {mode === 'screenshot' ? (
        <div className="mt-5">
          <ScreenshotGuide forceOpen={notice?.tone === 'bad'} />
          <div className="mt-4">
            <FileUpload onFileUpload={handleFile} isProcessing={isProcessing} progress={progress} />
          </div>
          <p className="mt-4 text-xs text-white/50 leading-relaxed">
            Runs on your device, the screenshot is never uploaded. Entries that don&apos;t
            count toward GPA (IP, S, U, credit transfers…) are skipped automatically.
          </p>
        </div>
      ) : (
        <div className="mt-2">
          <ul className="divide-y divide-white/10">
            {GRADES.map(grade => {
              const count = parseInt(counts[grade.letter], 10) || 0;
              return (
                <li key={grade.letter} className="flex items-center justify-between py-2.5">
                  <div className="flex items-baseline gap-2.5">
                    <span className="text-lg font-semibold w-9">{grade.letter}</span>
                    <span className="text-xs text-white/50">{grade.points.toFixed(1)} pts</span>
                  </div>
                  <div className="flex items-center rounded-full bg-white text-slate-900">
                    <button
                      type="button"
                      aria-label={`Fewer ${grade.letter} courses`}
                      onClick={() => setCount(grade.letter, count - 1)}
                      className="w-10 h-10 flex items-center justify-center active:scale-95 transition-transform disabled:opacity-30"
                      disabled={count === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-7 text-center text-[15px] font-semibold tabular-nums">
                      {count}
                    </span>
                    <button
                      type="button"
                      aria-label={`More ${grade.letter} courses`}
                      onClick={() => setCount(grade.letter, count + 1)}
                      className="w-10 h-10 flex items-center justify-center active:scale-95 transition-transform"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={() => setGpa(computeGpa(counts))}
            disabled={totalCourses === 0}
            className="mt-5 w-full rounded-full bg-white text-slate-900 font-semibold py-4 text-[15px] active:scale-[0.99] transition-transform disabled:opacity-40"
          >
            Calculate GPA
          </button>
        </div>
      )}

      {gpa !== null && (
        <div className="mt-7 pt-6 border-t border-white/15 text-center">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-mint-200">
            Your GPA
          </p>
          <p className="mt-1 text-[64px] leading-none font-semibold tabular-nums">
            {gpa.toFixed(2)}
          </p>
          <p className="mt-2 text-sm text-white/60">
            out of 4.5 · {totalCourses} course{totalCourses === 1 ? '' : 's'}
          </p>
        </div>
      )}
    </section>
  );
};

export default GpaTab;
